import type { IntrospectionQuery } from 'graphql';
import { buildClientSchema } from 'graphql';
import { Client, fetchExchange } from '@urql/core';
import { retryExchange } from '@urql/exchange-retry';

import {
  makeIntrospectionQuery,
  makeIntrospectSupportQuery,
  toSupportedFeatures,
  introspectionToSupportedFeatures,
  NO_SUPPORTED_FEATURES,
} from './introspection';

import type { SupportedFeatures, IntrospectSupportQueryData } from './introspection';

import { toError } from '../helpers';
import type { SchemaLoader, SchemaLoaderResult, OnSchemaUpdate, OnSchemaError } from './types';

interface LoadFromURLConfig {
  name?: string;
  url: URL | string;
  headers?: HeadersInit;
  interval?: number;
}

export function loadFromURL(config: LoadFromURLConfig): SchemaLoader {
  const interval = config.interval || 60_000;
  const subscriptions = new Set<OnSchemaUpdate>();
  const errorSubscriptions = new Set<OnSchemaError>();

  let timeoutID: NodeJS.Timeout | null = null;
  let supportedFeatures: SupportedFeatures | null = null;
  let result: SchemaLoaderResult | null = null;

  const client = new Client({
    url: `${config.url}`,
    fetchOptions: { headers: config.headers },
    exchanges: [
      retryExchange({
        initialDelayMs: 200,
        maxDelayMs: 1_500,
        maxNumberAttempts: 3,
        retryWith(error, operation) {
          if (error.networkError) process.env.NODE_TLS_REJECT_UNAUTHORIZED = '0';
          return operation;
        },
      }),
      fetchExchange,
    ],
  });

  const scheduleUpdate = () => {
    if (subscriptions.size && !timeoutID) {
      timeoutID = setTimeout(async () => {
        timeoutID = null;
        try {
          result = await load();
        } catch (error) {
          result = null;
          for (const subscriber of errorSubscriptions) subscriber(toError(error));
        }
        if (result) for (const subscriber of subscriptions) subscriber(result);
      }, interval);
    }
  };

  const introspect = async (support: SupportedFeatures): Promise<SchemaLoaderResult> => {
    const query = makeIntrospectionQuery(support);
    const introspectionResult = await client.query<IntrospectionQuery>(query, {});
    try {
      if (introspectionResult.error) {
        throw introspectionResult.error;
      } else if (introspectionResult.data) {
        const introspection = introspectionResult.data;
        return {
          introspection: {
            ...introspection,
            name: config.name,
          },
          schema: buildClientSchema(introspection, { assumeValid: true }),
        };
      } else {
        throw new Error(
          'Executing introspection against API failed.\n' +
            'The API failed to return any schema data or error.'
        );
      }
    } finally {
      scheduleUpdate();
    }
  };

  const load = async (): Promise<SchemaLoaderResult> => {
    if (!supportedFeatures) {
      const query = makeIntrospectSupportQuery();
      const supportResult = await client.query<IntrospectSupportQueryData>(query, {});
      if (supportResult.error && supportResult.error.graphQLErrors.length > 0) {
        try {
          // If we failed to determine support, we do a regular introspection
          const { introspection } = await introspect(NO_SUPPORTED_FEATURES);
          supportedFeatures = introspectionToSupportedFeatures(introspection);
        } catch (_error) {
          // If this also failed, we assume no supported features
          supportedFeatures = NO_SUPPORTED_FEATURES;
        }
      } else if (supportResult.data && !supportResult.error) {
        // Succeeding the support query, we get the supported features
        supportedFeatures = toSupportedFeatures(supportResult.data);
      } else if (supportResult.error) {
        // On misc. error, we rethrow and reset supported features
        supportedFeatures = null;
        throw supportResult.error;
      } else {
        // Otherwise we assume no features are supported
        supportedFeatures = NO_SUPPORTED_FEATURES;
      }
    }
    return introspect(supportedFeatures);
  };

  return {
    get name() {
      return config.name;
    },
    async load(reload?: boolean) {
      return reload || !result ? (result = await load()) : result;
    },
    notifyOnUpdate(onUpdate, onError) {
      subscriptions.add(onUpdate);
      if (onError) errorSubscriptions.add(onError);
      return () => {
        subscriptions.delete(onUpdate);
        if (onError) errorSubscriptions.delete(onError);
        if (!subscriptions.size && timeoutID) {
          clearTimeout(timeoutID);
          timeoutID = null;
        }
      };
    },
    async loadIntrospection() {
      const result = await this.load();
      return result && result.introspection;
    },
    async loadSchema() {
      const result = await this.load();
      return result && result.schema;
    },
  };
}
